import { useMemo, useState } from 'react'
import type { AgentOutputProps } from '../types'
import { ClipboardIcon, CheckIcon } from '@heroicons/react/24/outline'

/**
 * CodeRenderer — office-assembly and any agent whose output is raw code.
 *
 * Shows a line-numbered code block with a copy button. No syntax highlighting
 * (would require adding a highlighter dep); language label is shown when
 * available so the viewer knows what they're looking at.
 */

interface CodePayload {
  code?: string
  script?: string
  source?: string
  language?: string
  filename?: string
}

function pick(result: any): CodePayload | null {
  if (!result) return null
  const r = result
  const ad = r.agent_data || {}
  for (const src of [r, ad]) {
    if (!src) continue
    for (const key of ['code', 'script', 'source', 'fixed_script', 'fixed_source']) {
      if (typeof src[key] === 'string' && src[key].length > 0) {
        return { code: src[key], language: src.language, filename: src.filename }
      }
    }
  }
  // Last resort: if output looks like code, use it.
  if (typeof r.output === 'string' && r.output.length > 40) {
    return { code: r.output }
  }
  return null
}

export default function CodeRenderer(props: AgentOutputProps) {
  const payload = useMemo(() => pick(props.result), [props.result])
  const [copied, setCopied] = useState(false)

  if (!payload || !payload.code) {
    return (
      <div className="text-sm text-muted-foreground italic">
        No code found in the agent result.
      </div>
    )
  }

  const lines = payload.code.split('\n')
  const langLabel = payload.language || guessLanguage(payload.code)

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(payload.code!)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {}
  }

  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2 bg-muted/40 border-b border-border">
        <div className="text-xs text-muted-foreground font-mono">
          {payload.filename ? payload.filename : langLabel}
          <span className="ml-3 opacity-60">{lines.length} lines</span>
        </div>
        <button
          onClick={copy}
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          {copied ? <CheckIcon className="w-3 h-3" /> : <ClipboardIcon className="w-3 h-3" />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <pre className="text-xs font-mono overflow-auto max-h-[520px] bg-card">
        {lines.map((line, i) => (
          <div key={i} className="flex hover:bg-muted/30">
            <span className="select-none text-right pr-3 pl-2 text-muted-foreground/50 w-10 shrink-0 border-r border-border/40">
              {i + 1}
            </span>
            <span className="pl-3 pr-4 whitespace-pre text-foreground flex-1">{line || ' '}</span>
          </div>
        ))}
      </pre>
    </div>
  )
}

// Heuristic language guess just for the header label — no real parsing.
function guessLanguage(code: string): string {
  const head = code.slice(0, 400)
  if (/\bdef\s+\w+\s*\(|\bimport\s+\w+|\bfrom\s+\w+\s+import/.test(head)) return 'python'
  if (/\\documentclass|\\begin\{document\}|\\section\{/.test(head)) return 'latex'
  if (/\bconst\s+\w+\s*=|\bfunction\s+\w+\s*\(|=>/.test(head)) return 'javascript'
  if (/^\s*\{[\s\S]*"[^"]+"\s*:/.test(head)) return 'json'
  return 'text'
}
